import { useCallback, useRef, useState } from 'react'

export type SwipeDirection = 'left' | 'right' | null

// `number` is treated as pixels; a string like '40%' is resolved against the
// swiping element's width at pointerdown.
export type SwipeSize = number | `${number}%`

interface UseSwipeOptions {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  threshold?: SwipeSize
  maxReveal?: SwipeSize
  disabled?: boolean
  shouldStart?: (e: PointerEvent) => boolean
}

function resolveSize(size: SwipeSize, refWidth: number): number {
  if (typeof size === 'number') return size
  const pct = parseFloat(size)
  return (pct / 100) * refWidth
}

interface UseSwipeResult {
  bind: {
    onPointerDown: (e: React.PointerEvent) => void
  }
  dx: number
  swiping: boolean
  direction: SwipeDirection
}

export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 80,
  maxReveal = 100,
  disabled = false,
  shouldStart,
}: UseSwipeOptions): UseSwipeResult {
  const [dx, setDx] = useState(0)
  const [swiping, setSwiping] = useState(false)
  const [direction, setDirection] = useState<SwipeDirection>(null)

  const startX = useRef(0)
  const startY = useRef(0)
  const captured = useRef(false)
  const abandoned = useRef(false)
  const pointerId = useRef<number | null>(null)

  const reset = useCallback(() => {
    setDx(0)
    setSwiping(false)
    setDirection(null)
    captured.current = false
    abandoned.current = false
    pointerId.current = null
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return
      if (shouldStart && !shouldStart(e.nativeEvent)) return
      startX.current = e.clientX
      startY.current = e.clientY
      captured.current = false
      abandoned.current = false
      pointerId.current = e.pointerId

      const el = e.currentTarget as HTMLElement
      // Resolve percent-based sizes against the row width captured at start.
      // Frozen for the duration of the swipe so a mid-swipe layout change
      // can't shift the threshold under the user's finger.
      const refWidth = el.getBoundingClientRect().width
      const thresholdPx = resolveSize(threshold, refWidth)
      const maxRevealPx = resolveSize(maxReveal, refWidth)

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId.current) return
        if (abandoned.current) return

        const deltaX = ev.clientX - startX.current
        const deltaY = ev.clientY - startY.current

        if (!captured.current) {
          if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 6) {
            abandoned.current = true
            return
          }
          if (Math.abs(deltaX) > 6) {
            captured.current = true
            ev.preventDefault()
          } else {
            return
          }
        }

        ev.preventDefault()
        const clamped = Math.max(-maxRevealPx, Math.min(maxRevealPx, deltaX))
        setDx(clamped)
        setSwiping(true)
        setDirection(clamped < 0 ? 'left' : clamped > 0 ? 'right' : null)
      }

      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId.current) return
        el.removeEventListener('pointermove', onMove)
        el.removeEventListener('pointerup', onUp)
        el.removeEventListener('pointercancel', onUp)

        if (captured.current) {
          const deltaX = ev.clientX - startX.current
          if (deltaX <= -thresholdPx && onSwipeLeft) onSwipeLeft()
          else if (deltaX >= thresholdPx && onSwipeRight) onSwipeRight()
        }

        reset()
      }

      el.addEventListener('pointermove', onMove, { passive: false })
      el.addEventListener('pointerup', onUp)
      el.addEventListener('pointercancel', onUp)
    },
    [disabled, maxReveal, threshold, onSwipeLeft, onSwipeRight, reset, shouldStart]
  )

  return { bind: { onPointerDown }, dx, swiping, direction }
}
